import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Project } from '../types/project';
import { useAuth } from './useAuth';

interface UseProjectsReturn {
  ownedProjects: Project[];
  sharedProjects: Project[];
  allProjects: Project[];
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and subscribe to user's projects (owned and collaborative)
 * Uses real-time Firestore listeners for live updates
 * Uses two separate queries to avoid or() + orderBy() index complexity
 */
export const useProjects = (): UseProjectsReturn => {
  const { user } = useAuth();
  const [ownedProjects, setOwnedProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state if no user
    if (!user) {
      console.log('[useProjects] No user, resetting state');
      setOwnedProjects([]);
      setSharedProjects([]);
      setLoading(false);
      return;
    }

    console.log('[useProjects] Setting up listeners for user:', user.uid);

    let loadingCount = 2; // Track two queries
    const checkLoading = () => {
      loadingCount--;
      console.log('[useProjects] Loading count:', loadingCount);
      if (loadingCount === 0) {
        setLoading(false);
      }
    };

    try {
      // Query 1: Projects owned by the user
      const ownedQuery = query(
        collection(db, 'projects'),
        where('ownerId', '==', user.uid),
        orderBy('updatedAt', 'desc')
      );

      console.log('[useProjects] Created ownedQuery for user:', user.uid);

      // Query 2: Projects where user is a collaborator
      const sharedQuery = query(
        collection(db, 'projects'),
        where('collaborators', 'array-contains', user.uid),
        orderBy('updatedAt', 'desc')
      );

      console.log('[useProjects] Created sharedQuery for user:', user.uid);

      // Set up listener for owned projects
      const unsubscribeOwned = onSnapshot(
        ownedQuery,
        (snapshot) => {
          console.log('[useProjects] Owned projects snapshot received:', {
            size: snapshot.size,
            empty: snapshot.empty,
            docs: snapshot.docs.length
          });

          const owned: Project[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            console.log('[useProjects] Owned project doc:', {
              id: doc.id,
              ownerId: data.ownerId,
              name: data.name,
              updatedAt: data.updatedAt,
              createdAt: data.createdAt
            });

            return {
              projectId: doc.id,
              ownerId: data.ownerId,
              collaborators: data.collaborators || [],
              collaboratorEmails: data.collaboratorEmails || [],
              name: data.name,
              description: data.description,
              status: data.status,
              researchReportUrl: data.researchReportUrl,
              researchReportMd: data.researchReportMd,
              createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(data.createdAt),
              updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : new Date(data.updatedAt),
            };
          });

          console.log('[useProjects] Setting owned projects:', owned.length);
          setOwnedProjects(owned);
          setError(null);
          checkLoading();
        },
        (err) => {
          console.error('[useProjects] Error fetching owned projects:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch owned projects'));
          checkLoading();
        }
      );

      // Set up listener for shared projects
      const unsubscribeShared = onSnapshot(
        sharedQuery,
        (snapshot) => {
          console.log('[useProjects] Shared projects snapshot received:', {
            size: snapshot.size,
            empty: snapshot.empty,
            docs: snapshot.docs.length
          });

          const shared: Project[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            console.log('[useProjects] Shared project doc:', {
              id: doc.id,
              ownerId: data.ownerId,
              name: data.name
            });

            return {
              projectId: doc.id,
              ownerId: data.ownerId,
              collaborators: data.collaborators || [],
              collaboratorEmails: data.collaboratorEmails || [],
              name: data.name,
              description: data.description,
              status: data.status,
              researchReportUrl: data.researchReportUrl,
              researchReportMd: data.researchReportMd,
              createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(data.createdAt),
              updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : new Date(data.updatedAt),
            };
          });

          console.log('[useProjects] Setting shared projects:', shared.length);
          setSharedProjects(shared);
          setError(null);
          checkLoading();
        },
        (err) => {
          console.error('[useProjects] Error fetching shared projects:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch shared projects'));
          checkLoading();
        }
      );

      // Cleanup both listeners on unmount
      return () => {
        unsubscribeOwned();
        unsubscribeShared();
      };
    } catch (err) {
      console.error('Error setting up projects listener:', err);
      setError(err instanceof Error ? err : new Error('Failed to set up projects listener'));
      setLoading(false);
    }
  }, [user]);

  return {
    ownedProjects,
    sharedProjects,
    allProjects: [...ownedProjects, ...sharedProjects],
    loading,
    error,
  };
};
